/**
 * 404 Error component for public video share pages (/s/$slug)
 * Displays a friendly message when a video may not have been uploaded yet
 */

import { Box, Container, Paper, Typography, Button } from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";

export function VideoNotYetUploadedError() {
  const handleReload = () => {
    window.location.reload();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Decorative background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-10%",
          right: "-5%",
          width: "40%",
          height: "40%",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          filter: "blur(60px)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-10%",
          left: "-5%",
          width: "35%",
          height: "35%",
          borderRadius: "50%",
          background: "rgba(255, 255, 255, 0.1)",
          filter: "blur(60px)",
        }}
      />

      <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 4, md: 6 },
            borderRadius: 4,
            textAlign: "center",
            backgroundColor: "rgba(255, 255, 255, 0.98)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Hourglass Icon */}
          <Box
            sx={{
              fontSize: "5rem",
              mb: 2,
            }}
          >
            <span role="img" aria-label="hourglass">
              ⏳
            </span>
          </Box>

          {/* Error Message */}
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 2,
            }}
          >
            Video Not Available Yet
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mb: 4,
              fontSize: "1.1rem",
              lineHeight: 1.6,
            }}
          >
            This video may not have been uploaded yet. Please wait a moment and
            try again.
          </Typography>

          {/* Reload Button */}
          <Button
            variant="contained"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={handleReload}
            sx={{
              fontWeight: 700,
              fontSize: "1.1rem",
              py: 1.5,
              px: 4,
              borderRadius: 2,
              background: "linear-gradient(45deg, #667eea 30%, #764ba2 90%)",
              boxShadow: "0 3px 5px 2px rgba(102, 126, 234, .3)",
              transition: "all 0.3s ease",
              "&:hover": {
                background: "linear-gradient(45deg, #5a6fd6 30%, #6a3f9a 90%)",
                transform: "translateY(-2px)",
                boxShadow: "0 6px 10px 4px rgba(102, 126, 234, .3)",
              },
            }}
          >
            Reload Page
          </Button>

          {/* Help text */}
          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              If the video still doesn't appear after a few minutes, please
              contact the person who shared this link with you.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}
